import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, authedFetch, formatRand, resolveImageUrl } from "../../lib/api";

const empty = {
  name: "",
  category: "tees",
  description: "",
  images: [],
  price: 0,
  sale_price: null,
  sizes: [],
  colors: [],
  stock_status: "in_stock",
  inventory: null,
  published: true,
  featured: false,
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAll = () => {
    setLoading(true);
    api
      .get("/admin/products")
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing({
      ...p,
      sale_price: p.sale_price ?? null,
      sizes: p.sizes || [],
      colors: p.colors || [],
      images: p.images || [],
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...editing,
      price: Number(editing.price) || 0,
      sale_price: editing.sale_price === "" || editing.sale_price === null ? null : Number(editing.sale_price),
      inventory:
        editing.category === "headwear"
          ? (editing.inventory === "" || editing.inventory === null || editing.inventory === undefined
              ? null
              : Math.max(0, parseInt(editing.inventory, 10) || 0))
          : null,
      sizes: typeof editing.sizes === "string" ? editing.sizes.split(",").map((s) => s.trim()).filter(Boolean) : editing.sizes,
      colors: typeof editing.colors === "string" ? editing.colors.split(",").map((s) => s.trim()).filter(Boolean) : editing.colors,
      images: editing.images.filter((u) => u && u.trim() !== ""),
    };
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const duplicate = async (p) => {
    const payload = {
      name: `${p.name} (Copy)`,
      category: p.category,
      description: p.description || "",
      images: p.images || [],
      price: Number(p.price) || 0,
      sale_price: p.sale_price ?? null,
      sizes: p.sizes || [],
      colors: p.colors || [],
      stock_status: p.stock_status,
      inventory: p.inventory ?? null,
      published: false,
      featured: false,
    };
    try {
      await api.post("/admin/products", payload);
      toast.success("Product duplicated (unpublished)");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not duplicate");
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const token = localStorage.getItem("swk_admin_token");
    const newPaths = [];
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await authedFetch(`${API_BASE}/admin/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        newPaths.push(data.url);
      }
      setEditing((e) => ({ ...e, images: [...(e.images || []), ...newPaths] }));
      toast.success(`Uploaded ${newPaths.length} image${newPaths.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setEditing((e) => ({ ...e, images: e.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, dir) => {
    setEditing((e) => {
      const next = [...e.images];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return e;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...e, images: next };
    });
  };

  return (
    <div data-testid="admin-products-page">
      <div className="border-b hairline pb-6 mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline mb-2">Catalog</div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Products</h1>
        </div>
        <button onClick={openNew} className="btn-primary self-start" data-testid="admin-add-product-btn">
          <Plus size={14} strokeWidth={2} /> New Product
        </button>
      </div>

      <div className="border hairline overflow-x-auto" style={{ background: "#fff" }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Price</th>
              <th>Sale</th>
              <th>Stock</th>
              <th>Published</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="overline text-black/40 text-center py-8">Loading…</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={7} className="text-black/40 text-center py-8">No products</td></tr>
            ) : products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                <td className="flex items-center gap-3">
                  <div className="w-12 h-14" style={{ background: "var(--bg-alt)" }}>
                    {p.images?.[0] && (
                      <img src={resolveImageUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div>
                    <div className="font-display uppercase">{p.name}</div>
                    <div className="text-xs text-black/55">{p.slug}</div>
                  </div>
                </td>
                <td className="overline">{p.category}</td>
                <td className="font-display">{formatRand(p.price)}</td>
                <td>
                  {p.sale_price ? (
                    <span className="status-pill solid">{formatRand(p.sale_price)}</span>
                  ) : <span className="text-black/40">—</span>}
                </td>
                <td><span className="status-pill muted">{p.stock_status.replace("_", " ")}</span></td>
                <td>{p.published ? "Yes" : "No"}</td>
                <td>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => duplicate(p)} className="p-2 hover:bg-black/5" data-testid={`admin-duplicate-${p.id}`} title="Duplicate"><Copy size={14} /></button>
                    <button onClick={() => openEdit(p)} className="p-2 hover:bg-black/5" data-testid={`admin-edit-${p.id}`} title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => remove(p.id)} className="p-2 hover:bg-black/5" data-testid={`admin-delete-${p.id}`} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="admin-product-dialog">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="overline mb-1">{editing.id ? "Edit" : "New"} Product</div>
                <h2 className="font-display text-2xl uppercase tracking-tighter">{editing.name || "Untitled"}</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-black/5" data-testid="admin-product-dialog-close"><X size={18} /></button>
            </div>
            <div className="grid gap-5">
              <div>
                <label className="overline block mb-2">Name</label>
                <input className="input-box" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-product-name" />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Category</label>
                  <select className="input-box" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="admin-product-category">
                    <option value="tees">Tees</option>
                    <option value="headwear">Headwear</option>
                    <option value="hoodies">Hoodies</option>
                    <option value="members">Members Only</option>
                  </select>
                </div>
                <div>
                  <label className="overline block mb-2">Stock</label>
                  <select className="input-box" value={editing.stock_status} onChange={(e) => setEditing({ ...editing, stock_status: e.target.value })} data-testid="admin-product-stock">
                    <option value="in_stock">In stock</option>
                    <option value="low_stock">Low stock</option>
                    <option value="out_of_stock">Out of stock</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Price (R)</label>
                  <input type="number" className="input-box" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="admin-product-price" />
                </div>
                <div>
                  <label className="overline block mb-2">Sale Price (R) — leave blank to remove</label>
                  <input type="number" className="input-box" value={editing.sale_price ?? ""} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })} data-testid="admin-product-sale-price" />
                </div>
              </div>
              {editing.category === "headwear" && (
                <div>
                  <label className="overline block mb-2">Inventory (units) — leave blank for untracked</label>
                  <input
                    type="number"
                    min={0}
                    className="input-box"
                    placeholder="e.g. 25"
                    value={editing.inventory ?? ""}
                    onChange={(e) => setEditing({ ...editing, inventory: e.target.value })}
                    data-testid="admin-product-inventory"
                  />
                  <p className="mt-2 text-xs text-black/55">Auto-decrements when orders are marked paid. When it reaches 0, the item shows SOLD OUT on the store.</p>
                </div>
              )}
              <div>
                <label className="overline block mb-2">Description</label>
                <textarea rows={3} className="input-box" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="admin-product-description" />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2">Sizes (comma separated)</label>
                  <input className="input-box" value={Array.isArray(editing.sizes) ? editing.sizes.join(", ") : editing.sizes} onChange={(e) => setEditing({ ...editing, sizes: e.target.value })} data-testid="admin-product-sizes" />
                </div>
                <div>
                  <label className="overline block mb-2">Colors (comma separated)</label>
                  <input className="input-box" value={Array.isArray(editing.colors) ? editing.colors.join(", ") : editing.colors} onChange={(e) => setEditing({ ...editing, colors: e.target.value })} data-testid="admin-product-colors" />
                </div>
              </div>

              {/* IMAGE UPLOAD */}
              <div>
                <label className="overline block mb-2">Product Images</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(e) => uploadFiles(e.target.files)}
                  className="hidden"
                  data-testid="admin-product-image-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-secondary"
                  data-testid="admin-product-upload-btn"
                >
                  {uploading ? (
                    <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload size={14} strokeWidth={2} /> Upload Images</>
                  )}
                </button>
                <p className="mt-2 text-xs text-black/55">JPG, PNG or WebP · up to 8 MB each · drag to reorder</p>
                {editing.images?.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-product-image-grid">
                    {editing.images.map((src, idx) => (
                      <div key={`${src}-${idx}`} className="relative group border hairline" style={{ aspectRatio: "4/5", background: "var(--bg-alt)" }} data-testid={`admin-product-image-tile-${idx}`}>
                        <img src={resolveImageUrl(src)} alt="" className="w-full h-full object-cover" />
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 status-pill solid" style={{ fontSize: "0.55rem" }}>Cover</span>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} className="bg-white text-black px-2 py-1 text-xs disabled:opacity-40" data-testid={`admin-image-up-${idx}`}>↑</button>
                          <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === editing.images.length - 1} className="bg-white text-black px-2 py-1 text-xs disabled:opacity-40" data-testid={`admin-image-down-${idx}`}>↓</button>
                          <button type="button" onClick={() => removeImage(idx)} className="bg-white text-black px-2 py-1 text-xs" data-testid={`admin-image-remove-${idx}`}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-6 items-center">
                <label className="overline flex items-center gap-2">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="admin-product-published" /> Published
                </label>
                <label className="overline flex items-center gap-2">
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="admin-product-featured" /> Featured
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t hairline">
                <button onClick={() => setOpen(false)} className="btn-secondary" data-testid="admin-product-cancel">Cancel</button>
                <button onClick={save} disabled={uploading} className="btn-primary" data-testid="admin-product-save">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
